// In src/OptionsPanel.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings, X, Save } from 'lucide-react';
import config from './config';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from 'react-i18next';

// --- Reusable Sub-Components ---

const SliderControl = ({ label, value, onChange, min, max, step, formatter }) => {
    const displayValue = typeof value === 'number' ? formatter(value) : '...';
    return (
        <div className="option-row">
            <label>{label}: <span className="option-value">{displayValue}</span></label>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={typeof value === 'number' ? value : min}
                onChange={e => onChange(parseFloat(e.target.value))}
                className="slider"
            />
        </div>
    );
};

const ToggleSwitch = ({ label, checked, onChange }) => (
    <div className="option-row toggle-row">
        <label>{label}</label>
        <div className="toggle-switch">
            <input
                type="checkbox"
                id={`toggle-${label}`}
                className="toggle-switch-checkbox"
                checked={checked}
                onChange={e => onChange(e.target.checked)}
            />
            <label className="toggle-switch-label" htmlFor={`toggle-${label}`}>
                <span className="toggle-switch-inner" />
                <span className="toggle-switch-switch" />
            </label>
        </div>
    </div>
);

const RadioGroup = ({ options, selectedValue, onChange }) => (
    <div className="option-row radio-group">
        {options.map(option => (
            <React.Fragment key={option.value}>
                <input
                    type="radio"
                    id={`radio-${option.value}`}
                    name="clipMode"
                    value={option.value}
                    checked={selectedValue === option.value}
                    onChange={() => onChange(option.value)}
                />
                <label htmlFor={`radio-${option.value}`}>{option.label}</label>
            </React.Fragment>
        ))}
    </div>
);


// --- Main OptionsPanel Component ---

export default function OptionsPanel({ viewer, savePath, permission }) {
    const { t } = useTranslation();

    const [isOpen, setIsOpen] = useState(false);

    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);

    const importInputRef = useRef(null);
    const [background, setBackground] = useState('skybox');
    const [pointBudget, setPointBudget] = useState(1_000_000);
    const [fov, setFov] = useState(60);
    const [showLabels, setShowLabels] = useState(true);
    const [clipMode, setClipMode] = useState(window.Potree.ClipTask.HIGHLIGHT);
    const [moveSpeed, setMoveSpeed] = useState(400);

    // This effect syncs our React state with the Potree viewer's state
    useEffect(() => {
        if (!viewer) return;

        // --- Initial State Population ---
        setBackground(viewer.getBackground() || 'skybox');
        setPointBudget(viewer.getPointBudget() || 1000000); // Fallback to 1M
        setFov(viewer.getFOV());                     // Fallback to 60
        setShowLabels(viewer.measuringTool.showLabels);   // Booleans are usually safe
        setClipMode(viewer.getClipTask() || window.Potree.ClipTask.HIGHLIGHT);
        setMoveSpeed(viewer.getMoveSpeed() || 400);        // Fallback to 400

        // --- Event Listeners for External Changes ---
        const onMoveSpeedChange = (e) => setMoveSpeed(e.speed);

        viewer.addEventListener('move_speed_changed', onMoveSpeedChange);

        // --- Cleanup Function ---
        return () => {
            viewer.removeEventListener('move_speed_changed', onMoveSpeedChange);
        };
    }, [viewer]);

    // --- Handler for Saving ---
    const handleSave = async () => {
        if (!viewer || !savePath) {
            alert("Save path is not available. Cannot save.");
            return;
        }

        setIsSaving(true);
        console.log("Saving scene data to drive...");

        const Potree = window.Potree;
        const JSON5 = window.JSON5;

        try {
            const projectData = Potree.saveProject(viewer);
            const dataString = JSON5.stringify(projectData, null, "\t");
            const blob = new Blob([dataString], { type: 'application/json5' });

            const formData = new FormData();
            // The file must be named correctly for the drive to recognize it
            formData.append('file', blob, 'viewerDataSave.json5');
            formData.append('overwrite', 'true');

            // Use fetch to POST the data to the protected drive path
            const response = await fetch(`${config.API_BASE_URL}${savePath}`, {
                method: 'POST',
                body: formData,
                // NOTE: Do NOT set Content-Type header manually for FormData,
                // the browser does it automatically with the correct boundary.
            });

            if (response.ok) {
                console.log("Save successful!");
                setSaveSuccess(true);
                // After 2 seconds, revert the button back to its normal state
                setTimeout(() => {
                    setSaveSuccess(false);
                }, 2000);
            } else {
                const errorText = await response.text();
                throw new Error(`Failed to save: ${response.status} ${errorText}`);
            }
        } catch (error) {
            console.error("Error during save operation:", error);
            alert(`Error saving data: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    const handleImportClick = () => {
        // Programmatically click the hidden file input
        importInputRef.current.click();
    };

    const handleFileSelected = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        console.log(`Importing file: ${file.name}`);
        const reader = new FileReader();

        reader.onload = (e) => {
            const Potree = window.Potree;
            const JSON5 = window.JSON5; // Get the JSON5 library

            if (!JSON5) {
                console.error("JSON5 library not found. Cannot parse project file.");
                alert("Error: JSON5 library is missing, cannot import file.");
                return;
            }

            try {
                const content = e.target.result;
                // 1. Parse the file content using the JSON5 parser.
                const json = JSON5.parse(content);

                // 2. Check for the "Potree" type flag, just like the official loader.
                if (json.type === "Potree") {
                    // 3. Call the correct, high-level project loading function.
                    Potree.loadProject(viewer, json);
                    console.log("Import successful via Potree.loadProject.");
                } else {
                    console.warn("Import failed: The selected file is not a valid Potree project file (missing 'type' property).");
                    alert("Warning: The selected file does not appear to be a valid Potree project file.");
                }
            } catch (error) {
                console.error("Failed to import file:", error);
                alert("Error: Could not parse or load the selected file.");
            }
        };

        reader.readAsText(file);
        event.target.value = null;
    };


    // --- Handlers to update Potree from our UI ---
    const handleBgChange = (bg) => {
        setBackground(bg); // Update React state
        viewer.setBackground(bg); // Update Potree
    };

    const handleBudgetChange = (val) => {
        setPointBudget(val); // Update React state
        viewer.setPointBudget(val); // Update Potree
    };

    const handleFovChange = (val) => {
        setFov(val); // Update React state
        viewer.setFOV(val); // Update Potree
    };

    const handleShowLabelsChange = (val) => {
        setShowLabels(val); // Update React state
        viewer.measuringTool.showLabels = val; // Update Potree
    };

    const handleClipModeChange = (val) => {
        setClipMode(val); // Update React state
        viewer.setClipTask(val); // Update Potree
    };

    const handleMoveSpeedChange = (val) => {
        setMoveSpeed(val); // Update React state
        viewer.setMoveSpeed(val); // Update Potree
    };

    // --- Exponential Speed Mapping Logic from reskin.html ---
    const speedRange = { min: 1, max: 10000 };
    const sliderToSpeed = (sliderValue) => Math.pow(sliderValue, 4) * speedRange.max + speedRange.min;
    const speedToSlider = (actualSpeed) => {
        if (actualSpeed <= speedRange.min) return 0;
        return Math.pow(((actualSpeed - speedRange.min) / speedRange.max), 1 / 4);
    };

    return (
        <>
            {/* --- The Main Toggle Button --- */}
            <motion.button
                className="options-toggle-button"
                onClick={() => setIsOpen(!isOpen)}
                whileTap={{ scale: 0.8 }}
            >
                <Settings size={24} />
            </motion.button>

            {/* --- The Animated Panel --- */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        className="options-panel"
                        initial={{ y: "20%", opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: "20%", opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                    >
                        <div className="options-panel-header">
                            <h3>{t("optionsPanel.header")}</h3>
                            <button onClick={() => setIsOpen(false)}><X size={20} /></button>
                        </div>
                        <div className="options-panel-content">
                            <div className="options-section">
                                <h4>{t("optionsPanel.sections.data")}</h4>
                                <div className="prop-buttons">
                                    <button onClick={handleSave} disabled={!savePath || isSaving || saveSuccess || permission !== 'write'} className={saveSuccess ? 'save-success' : ''}>
                                        <Save size={16} />
                                        {isSaving ? t("optionsPanel.data.saving") : (saveSuccess ? t("optionsPanel.data.saved") : t("optionsPanel.data.save_view"))}
                                    </button>
                                    {/* <button onClick={handleImportClick}>Import Local File</button> */}
                                </div>
                            </div>

                            {/* --- Language Section --- */}
                            <div className="options-section">
                                <h4>{t("optionsPanel.sections.language")}</h4>
                                <LanguageSwitcher viewer={viewer} />
                            </div>

                            {/* --- Background Section --- */}
                            <div className="options-section">
                                <h4>{t("optionsPanel.sections.background")}</h4>
                                <div className="option-row background-options">
                                    <div className={`bg-choice ${background === 'gradient' ? 'active' : ''}`} onClick={() => handleBgChange('gradient')} style={{ background: 'linear-gradient(to bottom, #243538, #58686c)' }} title={t("optionsPanel.background.tooltips.gradient")} />
                                    <div className={`bg-choice ${background === 'skybox' ? 'active' : ''}`} onClick={() => handleBgChange('skybox')} style={{ backgroundImage: `url('/potree/resources/textures/skybox2/pz.jpg')` }} title={t("optionsPanel.background.tooltips.skybox")} />
                                    <div className={`bg-choice ${background === 'black' ? 'active' : ''}`} onClick={() => handleBgChange('black')} style={{ background: 'black' }} title={t("optionsPanel.background.tooltips.black")} />
                                    <div className={`bg-choice ${background === 'white' ? 'active' : ''}`} onClick={() => handleBgChange('white')} style={{ background: 'white' }} title={t("optionsPanel.background.tooltips.white")} />
                                </div>
                            </div>

                            {/* --- Appearance Section --- */}
                            <div className="options-section">
                                <h4>{t("optionsPanel.sections.appearance")}</h4>
                                <SliderControl label={t("optionsPanel.appearance.pointBudget")} value={pointBudget} onChange={handleBudgetChange} min={100000} max={8000000} step={1000} formatter={v => v.toLocaleString()} />
                                <SliderControl label={t("optionsPanel.appearance.fieldOfView")} value={fov} onChange={handleFovChange} min={20} max={100} step={1} formatter={v => `${v.toFixed(0)}°`} />
                                <ToggleSwitch label={t("optionsPanel.appearance.showLabels")} checked={showLabels} onChange={handleShowLabelsChange} />
                            </div>

                            <div className='options-section'>
                                <h4>{t("optionsPanel.sections.clipping")}</h4>
                                <RadioGroup
                                    options={[
                                        { value: window.Potree.ClipTask.HIGHLIGHT, label: t("optionsPanel.clipping.highlight") },
                                        { value: window.Potree.ClipTask.SHOW_INSIDE, label: t("optionsPanel.clipping.inside") },
                                        { value: window.Potree.ClipTask.SHOW_OUTSIDE, label: t("optionsPanel.clipping.outside") },
                                    ]}
                                    selectedValue={clipMode}
                                    onChange={handleClipModeChange}
                                />
                            </div>

                            {/* --- Navigation Section --- */}
                            <div className="options-section">
                                <h4>{t("optionsPanel.sections.navigation")}</h4>
                                <SliderControl
                                    label={t("optionsPanel.navigation.moveSpeed")}
                                    // The slider's value is the 0-1 representation
                                    value={speedToSlider(moveSpeed)}
                                    // On change, we immediately convert the new 0-1 value back to a real speed
                                    onChange={sliderVal => handleMoveSpeedChange(sliderToSpeed(sliderVal))}
                                    min={0} max={1} step={0.01}
                                    // The formatter ALWAYS displays the real speed, regardless of the slider's internal value
                                    formatter={_ => moveSpeed.toFixed(1)}
                                />
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
            <input
                type="file"
                ref={importInputRef}
                style={{ display: 'none' }}
                accept=".json5"
                onChange={handleFileSelected}
            />
        </>
    );
}